import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtWhen } from "../../lib/peApi";
import { PEFormRenderer } from "./PEForms";

const inputCls =
  "w-full rounded-sm border border-pe-sand/70 bg-white px-4 py-3 text-sm text-pe-forest outline-none focus:border-pe-gold transition-colors placeholder:text-pe-muted/60";

const STATUS_CLS = {
  confirmed: "bg-pe-cream text-pe-forest border-pe-gold/50",
  pending_payment: "bg-amber-50 text-amber-800 border-amber-200",
  pending_review: "bg-amber-50 text-amber-800 border-amber-200",
  completed: "bg-pe-cream text-pe-olive border-pe-sage/50",
  canceled: "bg-red-50 text-red-800 border-red-200",
  rescheduled: "bg-pe-cream text-pe-olive border-pe-sage/50",
  expired: "bg-red-50 text-red-800 border-red-200",
};

export default function PEGuestPage() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("email"); // email -> code -> in
  const [bookings, setBookings] = useState(null);
  const [policies, setPolicies] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("/policies").then(setPolicies).catch(() => {});
  }, []);

  const requestCode = async () => {
    setBusy(true);
    setErr("");
    try {
      await api("/guest/code/request", { method: "POST", body: { email } });
      setStage("code");
      setMsg("If we have bookings for that email, a 6-digit code is on its way (email, and text when configured).");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setErr("");
    try {
      await api("/guest/code/verify", { method: "POST", body: { email, code } });
      const d = await api("/guest/appointments");
      setBookings(d.bookings);
      setStage("in");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main-content" data-testid="pe-guest" className="min-h-screen bg-pe-ivory py-28 px-5">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <Link to="/prismatic-essence"><img src="/images/pe-logo.png" alt="Prismatic Essence" className="h-14 w-auto mx-auto mb-4" /></Link>
          <h1 className="font-pe text-3xl text-pe-forest">Guest appointment access</h1>
          <p className="text-[12px] text-pe-muted mt-3 max-w-md mx-auto leading-relaxed">
            Booked without an account? Enter the email you booked with and we'll send a secure one-time code.
            Your intake details stay private — only appointments are shown here. No account is ever required.
          </p>
        </div>

        {stage !== "in" && (
          <div className="bg-white border border-pe-sand/60 rounded-sm p-7 md:p-9 space-y-5 shadow-[0_16px_50px_rgba(44,58,40,0.07)]">
            {err && <p data-testid="pe-guest-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-4 py-3">{err}</p>}
            {msg && <p data-testid="pe-guest-notice" className="text-sm text-pe-forest bg-pe-cream border border-pe-gold/40 rounded-sm px-4 py-3">{msg}</p>}
            <label className="block">
              <span className="block font-sans text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">Email you booked with</span>
              <input data-testid="pe-guest-email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            {stage === "code" && (
              <label className="block">
                <span className="block font-sans text-[10px] tracking-[0.2em] uppercase text-pe-olive mb-1.5">6-digit code</span>
                <input data-testid="pe-guest-code" inputMode="numeric" maxLength={6} className={`${inputCls} tracking-[0.5em] text-center text-lg`} value={code} onChange={(e) => setCode(e.target.value)} />
              </label>
            )}
            {stage === "email" ? (
              <button data-testid="pe-guest-request" disabled={busy || !email.includes("@")} onClick={requestCode} className="w-full rounded-full bg-pe-forest text-pe-ivory py-3.5 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors disabled:opacity-50">
                {busy ? "Sending…" : "Email me a secure code"}
              </button>
            ) : (
              <div className="flex gap-3">
                <button data-testid="pe-guest-verify" disabled={busy || code.length !== 6} onClick={verify} className="flex-1 rounded-full bg-pe-forest text-pe-ivory py-3.5 font-sans text-[11px] tracking-[0.25em] uppercase hover:bg-pe-olive transition-colors disabled:opacity-50">
                  {busy ? "Checking…" : "View my appointments"}
                </button>
                <button onClick={requestCode} disabled={busy} className="rounded-full border border-pe-brass/60 px-5 text-[10px] tracking-[0.2em] uppercase text-pe-forest">Resend</button>
              </div>
            )}
            <p className="text-[12px] text-pe-muted text-center">
              Changed your mind about accounts? <Link to="/prismatic-essence/auth" className="text-pe-brass underline underline-offset-4">Create one</Link> with the same email and your guest bookings attach securely.
            </p>
          </div>
        )}

        {stage === "in" && bookings && (
          <div className="space-y-4" data-testid="pe-guest-appointments">
            <div className="flex items-center justify-between">
              <p className="text-[11px] tracking-[0.2em] uppercase text-pe-muted">{email}</p>
              <button onClick={async () => { await api("/guest/logout", { method: "POST" }); setStage("email"); setBookings(null); setCode(""); }} className="text-[10px] tracking-[0.2em] uppercase text-pe-olive underline underline-offset-4">Sign out</button>
            </div>
            {msg && <p className="text-sm text-pe-forest bg-pe-cream border border-pe-gold/40 rounded-sm px-4 py-3">{msg}</p>}
            {err && <p data-testid="pe-guest-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-sm px-4 py-3">{err}</p>}
            {!bookings.length && <p className="text-sm text-pe-muted">No appointments found for this email yet.</p>}
            {bookings.map((b) => (
              <GuestAppointment key={b.booking_id} booking={b} policies={policies} onDone={async (m) => { setMsg(m); setErr(""); const d = await api("/guest/appointments"); setBookings(d.bookings); }} onError={setErr} />
            ))}
            {policies && (
              <div className="rounded-sm border border-pe-sand/60 bg-white p-6 space-y-3" data-testid="pe-guest-policies">
                <p className="font-pe text-lg text-pe-forest">Cancellation & rescheduling policy</p>
                <p className="text-[12px] text-pe-body leading-relaxed">{policies.cancellation}</p>
                <p className="text-[12px] text-pe-body leading-relaxed">{policies.rescheduling}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

function GuestAppointment({ booking: b, policies, onDone, onError }) {
  const [mode, setMode] = useState(""); // "" | cancel | reschedule | forms
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [days, setDays] = useState({});
  const [newSlot, setNewSlot] = useState("");
  const [forms, setForms] = useState(null);
  const [formStatus, setFormStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const manageable = ["confirmed", "pending_review"].includes(b.status) && new Date(b.start) > new Date();

  useEffect(() => {
    if (mode === "reschedule") {
      api(`/availability?service_id=${b.service.id}&month=${month}`).then((d) => setDays(d.days)).catch(() => setDays({}));
    }
    if (mode === "forms" && !forms) {
      api(`/forms/definitions?service_id=${b.service.id}`).then((d) => setForms(d.forms)).catch(() => {});
      api("/guest/forms").then((d) => setFormStatus(d.status)).catch(() => {});
    }
  }, [mode, month, b.service.id, forms]);

  const cancel = async (confirmFee) => {
    setBusy(true);
    try {
      const r = await api(`/bookings/${b.booking_id}/cancel`, { method: "POST", body: { confirm_fee: confirmFee } });
      if (r.requires_fee && r.checkout_url) {
        window.location.href = r.checkout_url;
        return;
      }
      if (r.requires_fee) {
        onError(""); // fee explanation rendered via mode
        setMode("cancel-fee");
        return;
      }
      setMode("");
      await onDone("Your appointment has been cancelled — a confirmation is on its way to your email.");
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    if (!newSlot) return onError("Please choose a new date and time.");
    setBusy(true);
    try {
      const r = await api(`/bookings/${b.booking_id}/reschedule`, { method: "POST", body: { new_start: newSlot } });
      if (r.requires_fee && r.checkout_url) {
        window.location.href = r.checkout_url;
        return;
      }
      setMode("");
      await onDone(`Rescheduled to ${fmtWhen(r.booking.start)} — your $25 deposit moved with it.${r.note ? " " + r.note : ""}`);
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const hoursUntil = (new Date(b.start) - new Date()) / 36e5;
  const lateWindow = policies ? hoursUntil < policies.cancellation_window_hours : false;

  return (
    <div data-testid={`pe-guest-appointment-${b.booking_id}`} className="rounded-sm border border-pe-sand/60 bg-white p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-pe text-xl text-pe-forest">{b.service.name}</p>
          <p className="text-[13px] text-pe-muted">{fmtWhen(b.start)} · {b.service.duration_label}</p>
          <p className="text-[12px] text-pe-muted mt-1">
            {b.quote?.service_price ? `Service ${b.service.starting ? "from " : ""}${fmtMoney(b.quote.service_price)}` : "Price at consultation"}
            {b.quote?.addons?.filter((a) => a.amount > 0).map((a) => ` · ${a.label} +$${a.amount}`)}
            {" · "}Deposit {b.payment_status === "paid" ? "paid ✓" : "$25 due"}
            {b.quote?.balance !== null && b.quote?.balance !== undefined && ` · Balance ${fmtMoney(b.quote.balance)}`}
          </p>
          {b.status === "rescheduled" && b.rescheduled_to && <p className="text-[12px] text-pe-olive mt-1">Moved to a new time — see the linked booking below.</p>}
        </div>
        <span className={`rounded-full border px-3 py-1 text-[10px] tracking-[0.15em] uppercase ${STATUS_CLS[b.status] || ""}`}>{b.status.replace(/_/g, " ")}</span>
      </div>

      {manageable && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-pe-sand/50">
          <button data-testid={`pe-guest-reschedule-${b.booking_id}`} onClick={() => setMode(mode === "reschedule" ? "" : "reschedule")} className="rounded-full border border-pe-brass/60 px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase text-pe-forest hover:bg-pe-gold/10">Reschedule</button>
          <button data-testid={`pe-guest-cancel-${b.booking_id}`} onClick={() => setMode(mode === "cancel" ? "" : "cancel")} className="rounded-full border border-red-300 px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase text-red-800 hover:bg-red-50">Cancel</button>
          <button data-testid={`pe-guest-forms-${b.booking_id}`} onClick={() => setMode(mode === "forms" ? "" : "forms")} className="rounded-full border border-pe-sand px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase text-pe-olive hover:border-pe-gold">My forms</button>
        </div>
      )}

      {mode === "cancel" && (
        <div className="mt-4 rounded-sm border border-red-200 bg-red-50/60 p-5 space-y-3" data-testid={`pe-guest-cancel-panel-${b.booking_id}`}>
          <p className="text-[13px] text-pe-body leading-relaxed">{policies?.cancellation}</p>
          <p className="text-[13px] text-pe-forest">
            {lateWindow
              ? `You're within ${policies.cancellation_window_hours} hours of your appointment — a $${policies.cancellation_late_fee} late-cancellation fee applies, payable now.`
              : `You're more than ${policies?.cancellation_window_hours || 24} hours ahead — no fee applies.`}
          </p>
          <div className="flex gap-2">
            <button data-testid={`pe-guest-cancel-confirm-${b.booking_id}`} disabled={busy} onClick={() => cancel(lateWindow)} className="rounded-full bg-red-800 text-white px-5 py-2 text-[10px] tracking-[0.2em] uppercase disabled:opacity-50">
              {busy ? "Working…" : lateWindow ? `Pay $${policies.cancellation_late_fee} fee & cancel` : "Cancel my appointment"}
            </button>
            <button onClick={() => setMode("")} className="text-[10px] tracking-[0.2em] uppercase text-pe-muted">Keep it</button>
          </div>
        </div>
      )}

      {mode === "reschedule" && (
        <div className="mt-4 rounded-sm border border-pe-gold/40 bg-pe-cream/50 p-5 space-y-3" data-testid={`pe-guest-reschedule-panel-${b.booking_id}`}>
          <p className="text-[13px] text-pe-body leading-relaxed">{policies?.rescheduling}</p>
          {lateWindow && policies?.reschedule_late_rule === "unpublished" && (
            <p className="text-[12px] text-pe-muted italic">You're rescheduling within 24 hours — the house's late-rescheduling rule is being finalized and no fee applies today.</p>
          )}
          <div className="flex items-center gap-3">
            <input type="month" className={inputCls} value={month} onChange={(e) => { setMonth(e.target.value); setNewSlot(""); }} />
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {Object.keys(days).sort().map((d) => (
              <div key={d}>
                <p className="text-[10px] tracking-[0.2em] uppercase text-pe-olive">{new Date(d + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {days[d].map((iso) => (
                    <button key={iso} data-testid={`pe-guest-slot-${iso.slice(11, 16)}`} onClick={() => setNewSlot(iso)} className={`px-3 py-1.5 rounded-full text-[11px] border transition-colors ${newSlot === iso ? "bg-pe-forest text-pe-ivory border-pe-forest" : "border-pe-sand text-pe-body hover:border-pe-gold"}`}>
                      {new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {!Object.keys(days).length && <p className="text-[12px] text-pe-muted">No openings this month — try the next one.</p>}
          </div>
          <button data-testid={`pe-guest-reschedule-confirm-${b.booking_id}`} disabled={busy || !newSlot} onClick={reschedule} className="rounded-full bg-pe-forest text-pe-ivory px-5 py-2 text-[10px] tracking-[0.2em] uppercase disabled:opacity-50">
            {busy ? "Confirming…" : "Confirm new time"}
          </button>
        </div>
      )}

      {mode === "forms" && (
        <div className="mt-4 space-y-8 border-t border-pe-sand/50 pt-5" data-testid={`pe-guest-forms-panel-${b.booking_id}`}>
          {formStatus && (
            <p className="text-[12px] text-pe-muted">
              Consultation {formStatus.consultation.complete ? "complete ✓" : "incomplete"} · Intake {formStatus.intake.complete ? "complete ✓" : "incomplete"}
            </p>
          )}
          {(forms || []).map((def) => (
            <div key={def.key}>
              <p className="font-pe text-lg text-pe-forest mb-3">{def.title}</p>
              <PEFormRenderer
                definition={def}
                testPrefix={`pe-guest-form-${def.key}`}
                onSubmit={async (answers) => {
                  const r = await api(`/guest/forms/${def.key}`, { method: "PUT", body: { answers } });
                  setFormStatus(r.status);
                  await onDone(`${def.title} updated.`);
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
